/**
 * useVoiceRecorder — l'enregistreur vocal, effets branchés sur la machine `voiceRecorderMachine`.
 *
 * Ce qu'il branche, comme l'ancien enregistreur (`AudioRecordingContainer`) :
 *  - `expo-audio` (`useAudioRecorder`, un préréglage voix tiré de `HIGH_QUALITY`) : le natif, et sur le web `MediaRecorder`,
 *    en MP4/AAC quand le navigateur sait l'écrire, WebM en repli (`voiceFormat`) ;
 *  - la permission du micro (`requestRecordingPermissionsAsync`), et son refus, qui est un état
 *    (`denied`) et non une boîte : c'est l'écran qui le dit ;
 *  - la session audio partagée (`engine/shared/audioSession`), prise au début et rendue à chaque
 *    sortie, démontage compris ; `liveCallActive` dit qu'un appel la tient (LiveKit) ;
 *  - l'écran gardé allumé pendant l'enregistrement (natif) ;
 *  - le plafond (12 min par défaut) : arrivé au bout, l'enregistrement s'arrête et se GARDE ;
 *  - l'arrière-plan (natif) : l'app qui quitte le premier plan arrête l'enregistrement proprement
 *    et le GARDE, au lieu de le perdre sur une erreur du système (vu sur iPad) ; au retour, un
 *    enregistreur que le système a coupé est arrêté et gardé de même.
 *
 * Ce qu'il rend : l'état, la durée figée au dernier changement (`durationMs`) et le début du
 * segment en cours (`runningSince`, en ms de `Date.now()`), d'où un compteur tire le temps qui
 * court (`<RecorderClock>`) sans que l'écran qui porte le crochet se redessine chaque seconde ;
 * l'adresse du fichier et son vrai type ; l'erreur ; et les commandes.
 *
 * Rien de propre à un rôle, à un écran ni à un point d'API : l'envoi est l'affaire de la file
 * (`voiceOutbox`) ou de l'appelant.
 */
import { RecordingPresets, requestRecordingPermissionsAsync, useAudioRecorder } from "expo-audio"
import { activateKeepAwakeAsync, deactivateKeepAwake } from "expo-keep-awake"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AppState, Platform } from "react-native"

import { fetchResourceFromURI } from "../../data/utility/fileFromURI"
import {
    prepareRecordingSession,
    reassertLiveSession,
    releaseRecordingSession,
} from "../shared/audioSession/audioSessionCoordinator"
import { IS_WEB } from "../shared/platform"
import { TransitionTrail } from "../shared/telemetryMetadata"
import { pickWebRecorderMimeType, recordingFormat, type VoiceFormat } from "./voiceFormat"
import {
    initialVoiceRecorderState,
    isBelowMinimum,
    MAX_RECORDING_DURATION_MS,
    remainingMs,
    voiceRecorderReducer,
    type VoiceRecorderErrorState,
    type VoiceRecorderEvent,
    type VoiceRecorderNotice,
    type VoiceRecorderState,
    type VoiceRecorderStatus,
    type VoiceStopReason,
} from "./voiceRecorderMachine"
import { VoiceRecorderError, voiceTelemetry } from "./voiceTelemetry"

const KEEP_AWAKE_TAG = "VoiceRecorder"

const WEB_RECORDER_MIME_TYPE = IS_WEB
    ? pickWebRecorderMimeType(
          typeof MediaRecorder !== "undefined" ? (type) => MediaRecorder.isTypeSupported(type) : null
      )
    : null

// La voix, pas la musique : AAC dans un MP4 comme `HIGH_QUALITY`, mais en mono à 64 kbit/s. Le
// plafond de 12 min tient alors en ~6 Mo, sous la limite de 10 Mo d'un vocal côté serveur (à
// 128 kbit/s stéréo, tout vocal au-delà de ~10 min 40 était refusé).
const VOICE_BIT_RATE = 64000
const VOICE_PRESET = {
    ...RecordingPresets.HIGH_QUALITY,
    numberOfChannels: 1,
    bitRate: VOICE_BIT_RATE,
    web: { ...RecordingPresets.HIGH_QUALITY.web, bitsPerSecond: VOICE_BIT_RATE },
}

const RECORDER_OPTIONS = WEB_RECORDER_MIME_TYPE
    ? { ...VOICE_PRESET, web: { ...VOICE_PRESET.web, mimeType: WEB_RECORDER_MIME_TYPE } }
    : VOICE_PRESET

export type UseVoiceRecorderOptions = {
    /** Le plafond, en ms. Défaut : 12 minutes. */
    maxDurationMs?: number
    /** Une fin demandée en deçà se refuse (l'enregistrement se met en pause, `notice: "too_short"`). Défaut 0. */
    minDurationMs?: number
    /** Un appel en cours tient la session audio (LiveKit) : elle n'est pas reconfigurée. Défaut `false`. */
    liveCallActive?: boolean
    /** Natif : arrêter et garder l'enregistrement quand l'app passe en arrière-plan. Défaut `true`. */
    stopOnBackground?: boolean
}

export type VoiceRecorderController = {
    state: VoiceRecorderStatus
    /** La durée enregistrée au dernier changement d'état, en ms ; `recorded` : celle du fichier. */
    durationMs: number
    /** Le début du segment en cours (`Date.now()`), `null` hors enregistrement. */
    runningSince: number | null
    maxDurationMs: number
    /** Le fichier, une fois `recorded`. */
    uri: string | null
    /** Son vrai type, sans paramètres (`audio/mp4`, `audio/webm`). */
    mimeType: string | null
    /** `.m4a`, `.webm`… */
    extension: string | null
    error: VoiceRecorderErrorState | null
    notice: VoiceRecorderNotice | null
    /** `denied` : le système peut encore demander (sinon, les réglages de l'appareil). */
    canAskAgain: boolean
    /** L'arrêt est demandé, le fichier s'écrit. */
    stopping: boolean
    /** Demande le micro puis enregistre. Depuis `recorded`, recommence (l'ancien fichier est oublié). */
    start: () => Promise<void>
    pause: () => void
    resume: () => void
    /** Arrête et garde le fichier (`recorded`), ou se met en pause sous la durée minimale. */
    stop: () => Promise<void>
    /** Abandonne : l'enregistrement en cours s'arrête, tout revient à `idle`. */
    reset: () => Promise<void>
}

const isLive = (state: VoiceRecorderState) => state.status === "recording" || state.status === "paused"

/** Le format du fichier fini : sur le web, le type que porte le `Blob` écrit par `MediaRecorder`. */
async function formatOf(uri: string): Promise<VoiceFormat> {
    if (!IS_WEB) return recordingFormat(uri)
    try {
        const blob: Blob = await fetchResourceFromURI(uri)
        return recordingFormat(uri, blob.type || WEB_RECORDER_MIME_TYPE)
    } catch (error) {
        voiceTelemetry.breadcrumb("voice.format_unread", { reason: (error as Error)?.message ?? "unknown" })
        return recordingFormat(uri, WEB_RECORDER_MIME_TYPE)
    }
}

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error))

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): VoiceRecorderController {
    const {
        maxDurationMs = MAX_RECORDING_DURATION_MS,
        minDurationMs = 0,
        liveCallActive = false,
        stopOnBackground = true,
    } = options

    const recorder = useAudioRecorder(RECORDER_OPTIONS)
    const [state, setState] = useState<VoiceRecorderState>(initialVoiceRecorderState)
    // L'état lu par les commandes asynchrones : celui d'après le dernier événement, pas celui du rendu.
    const stateRef = useRef(state)
    const trailRef = useRef(new TransitionTrail())
    const liveRef = useRef(liveCallActive)
    liveRef.current = liveCallActive
    const sessionHeldRef = useRef(false)
    const mountedRef = useRef(true)

    const send = useCallback((event: VoiceRecorderEvent) => {
        const previous = stateRef.current
        const next = voiceRecorderReducer(previous, event)
        if (next === previous) return
        stateRef.current = next
        const transition = `${previous.status}→${next.status} (${event.type})`
        trailRef.current.push(transition)
        voiceTelemetry.breadcrumb("voice.recorder", {
            transition,
            durationMs: next.accumulatedMs,
            ...(next.mimeType != null ? { mimeType: next.mimeType } : {}),
        })
        if (mountedRef.current) setState(next)
    }, [])

    const report = useCallback((kind: VoiceRecorderError["kind"], cause: unknown) => {
        voiceTelemetry.error(new VoiceRecorderError(kind, cause), {
            transitions: trailRef.current.list(),
            platform: Platform.OS,
            liveCallActive: liveRef.current,
        })
    }, [])

    const releaseSession = useCallback(async () => {
        if (!sessionHeldRef.current) return
        sessionHeldRef.current = false
        try {
            await releaseRecordingSession(liveRef.current)
        } catch (error) {
            voiceTelemetry.breadcrumb("voice.session_release_failed", { reason: messageOf(error) })
        }
    }, [])

    const start = useCallback(async () => {
        const current = stateRef.current
        if (current.status === "requesting" || isLive(current)) return
        send({ type: "START_REQUESTED" })
        try {
            const permission = await requestRecordingPermissionsAsync()
            if (!permission.granted) {
                send({ type: "PERMISSION_DENIED", canAskAgain: permission.canAskAgain !== false })
                return
            }
            await prepareRecordingSession(liveRef.current)
            sessionHeldRef.current = true
            await recorder.prepareToRecordAsync()
            // `prepare` écrase la configuration iOS : pendant un appel, on remet celle que les deux tolèrent.
            await reassertLiveSession(liveRef.current)
            recorder.record()
            send({ type: "RECORDING_STARTED", at: Date.now() })
        } catch (error) {
            report("start_failed", error)
            await releaseSession()
            send({ type: "START_FAILED", message: messageOf(error) })
        }
    }, [recorder, send, report, releaseSession])

    const pause = useCallback(() => {
        const current = stateRef.current
        if (current.status !== "recording" || current.stopping) return
        try {
            recorder.pause()
            send({ type: "PAUSED", at: Date.now() })
        } catch (error) {
            report("pause_failed", error)
        }
    }, [recorder, send, report])

    const resume = useCallback(() => {
        const current = stateRef.current
        if (current.status !== "paused" || current.stopping) return
        try {
            recorder.record()
            send({ type: "RESUMED", at: Date.now() })
        } catch (error) {
            report("resume_failed", error)
        }
    }, [recorder, send, report])

    const stopWith = useCallback(
        async (reason: VoiceStopReason) => {
            const current = stateRef.current
            if (!isLive(current) || current.stopping) return
            const at = Date.now()
            if (reason === "user" && isBelowMinimum(current, at, minDurationMs)) {
                if (current.status === "recording") {
                    try {
                        recorder.pause()
                    } catch (error) {
                        report("pause_failed", error)
                    }
                }
                send({ type: "TOO_SHORT", at })
                return
            }
            send({ type: "STOP_REQUESTED", at, reason })
            // La durée que l'enregistreur a mesurée, lue avant l'arrêt (après, elle repart à zéro).
            const measuredMs = recorder.getStatus?.()?.durationMillis ?? null
            try {
                await recorder.stop()
                const uri = recorder.uri
                if (uri == null || uri === "") throw new Error("the recorder wrote no file")
                const format = await formatOf(uri)
                send({ type: "STOPPED", uri, ...format, measuredMs })
            } catch (error) {
                report("stop_failed", error)
                send({ type: "STOP_FAILED", message: messageOf(error) })
            } finally {
                // Laissée en configuration d'enregistrement, la session atténuerait tout son suivant.
                await releaseSession()
            }
        },
        [recorder, send, report, releaseSession, minDurationMs]
    )

    const stop = useCallback(() => stopWith("user"), [stopWith])

    const reset = useCallback(async () => {
        if (isLive(stateRef.current)) {
            try {
                await recorder.stop()
            } catch (error) {
                voiceTelemetry.breadcrumb("voice.cancel_stop_failed", { reason: messageOf(error) })
            }
            await releaseSession()
        }
        send({ type: "RESET" })
    }, [recorder, send, releaseSession])

    // Le plafond : arrivé au bout, l'enregistrement s'arrête et se garde.
    useEffect(() => {
        if (state.status !== "recording" || state.segmentStartedAt == null || state.stopping) return
        const timer = setTimeout(
            () => void stopWith("max_duration"),
            remainingMs(stateRef.current, Date.now(), maxDurationMs)
        )
        return () => clearTimeout(timer)
    }, [state.status, state.segmentStartedAt, state.stopping, maxDurationMs, stopWith])

    // L'arrière-plan (natif) : arrêté proprement et gardé, plutôt que perdu sur une erreur du système.
    useEffect(() => {
        if (IS_WEB || !stopOnBackground) return
        const subscription = AppState.addEventListener("change", (next) => {
            const current = stateRef.current
            if (!isLive(current) || current.stopping) return
            if (next === "background") {
                void stopWith("background")
                return
            }
            // De retour : un enregistreur que le système a coupé pendant l'absence est arrêté et gardé.
            if (next === "active" && current.status === "recording" && !recorder.isRecording) {
                void stopWith("background")
            }
        })
        return () => subscription.remove()
    }, [stopOnBackground, stopWith, recorder])

    // L'écran reste allumé pendant l'enregistrement (natif, comme l'ancien enregistreur).
    const recordingOrPaused = isLive(state)
    useEffect(() => {
        if (IS_WEB || !recordingOrPaused) return
        activateKeepAwakeAsync(KEEP_AWAKE_TAG).catch((error) =>
            voiceTelemetry.breadcrumb("voice.keep_awake_failed", { reason: messageOf(error) })
        )
        return () => {
            void deactivateKeepAwake(KEEP_AWAKE_TAG)
        }
    }, [recordingOrPaused])

    // Quitter l'écran en plein enregistrement rend la session : le crochet d'expo libère
    // l'enregistreur, seul le coordinateur rend la session.
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            void releaseSession()
        }
    }, [releaseSession])

    return useMemo(
        () => ({
            state: state.status,
            durationMs: state.accumulatedMs,
            runningSince: state.segmentStartedAt,
            maxDurationMs,
            uri: state.uri,
            mimeType: state.mimeType,
            extension: state.extension,
            error: state.error,
            notice: state.notice,
            canAskAgain: state.canAskAgain,
            stopping: state.stopping,
            start,
            pause,
            resume,
            stop,
            reset,
        }),
        [state, maxDurationMs, start, pause, resume, stop, reset]
    )
}
